import { FC, useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ModalConfirmPayment from '../modalConfirmPayment';
import { getOrderDetailedData, updateOrderStatus } from '../../services/orders';
import { OrderCardInListing } from '../../views/orders/Components';
import { IProductList, ORDER_STATUS } from '../../types/orders';
import GenericLoader from '../GenericLoader';
import { displayErrorNotification } from '../../utils/displayNudosStandardNotifications';
import './ShoppingList.scss';

interface IShoppingList {
  ordersList: IProductList[];
  refreshData: () => void;
}

const ShoppingList: FC<IShoppingList> = ({ ordersList, refreshData }: IShoppingList) => {
  const [paymentList, setPaymentList] = useState<IProductList[]>([]);
  const [confirmList, setConfirmList] = useState<IProductList[]>([]);
  const [deliverySuccessList, setDeliverySuccessList] = useState<IProductList[]>([]);
  const [cancelList, setCancelList] = useState<IProductList[]>([]);
  const [updateModalInfo, setUpdateModalInfo] = useState<IProductList>();
  const [updateDeliveryModal, setUpdateDeliveryModal] = useState<IProductList>();
  const [loadingUpdate, setLoadingUpdate] = useState<boolean>();
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const getList = () => {
    const payment = ordersList.filter(element => element.statusName === ORDER_STATUS.PENDING);
    if (payment) setPaymentList(payment);
    const confirm = ordersList.filter(element => element.statusName === ORDER_STATUS.ACTIVE);
    if (confirm) setConfirmList(confirm);

    const deliverySuccess = ordersList.filter(element => element.statusName === ORDER_STATUS.DELIVERED);
    if (deliverySuccess) setDeliverySuccessList(deliverySuccess);
    const cancel = ordersList.filter(element => element.statusName === ORDER_STATUS.CANCELLED);
    if (cancel) setCancelList(cancel);
  };

  const getOrderData = async (origin: IProductList) => {
    setLoadingData(true);
    try {
      const response = await getOrderDetailedData(Number(origin.id));
      if (response) {
        const originData: IProductList = {
          billingProvider: response?.billingMethod?.billingProvider,
          ...origin
        };
        setUpdateModalInfo(originData);
      }
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingData(false);
    }
  };

  const updateConfirm = (positionList: number) => {
    const origin = paymentList[positionList];
    if (
      origin &&
      origin?.editShipment &&
      !!origin?.editShipment?.length &&
      origin?.editShipment?.find(edit => edit?.platform === 'NODI')
    ) {
      return displayErrorNotification({
        customJSXMessage: <>No puedes mover la orden hasta revisar los cambios realizados por el usuario</>
      });
    }
    setConfirmList([origin, ...confirmList]);
    const newPaymentList = paymentList.filter(element => element.id !== origin.id);
    getOrderData(origin);
    setPaymentList([...newPaymentList]);
  };

  const updateDeliveryProcess = (positionList: number) => {
    const origin = confirmList[positionList];
    const newConfirmList = confirmList.filter(element => element.id !== origin.id);
    setConfirmList([...newConfirmList]);
  };

  const updateCancelList = async (positionList: number) => {
    const origin = paymentList[positionList];
    setCancelList([origin, ...cancelList]);
    const newPaymentList = paymentList.filter(element => element.id !== origin.id);
    setPaymentList([...newPaymentList]);
    await updateData({ data: origin, status: 4 });
  };

  const cancelOriginConfirmList = async (positionList: number) => {
    const origin = confirmList[positionList];
    setCancelList([origin, ...cancelList]);
    const newConfirmList = confirmList.filter(element => element.id !== origin.id);
    setConfirmList([...newConfirmList]);
    await updateData({ data: origin, status: 4 });
  };

  const restorOrderToConfirm = async (positionList: number) => {
    const origin = cancelList[positionList];
    setPaymentList([origin, ...paymentList]);
    const newCancelList = cancelList.filter(element => element.id !== origin.id);
    setCancelList([...newCancelList]);
    await updateData({ data: origin, status: 1 });
  };

  const updateData = async ({ data, status }: { data: IProductList; status: number }) => {
    setLoadingUpdate(true);
    try {
      const { invoiceNumber, paymentLink, committedPaymentDate, invoice, proofOfPayment } = data;
      const formData = new FormData();
      const bodyForm = {
        invoiceNumber: invoiceNumber,
        paymentLink: paymentLink,
        committedPaymentDate: committedPaymentDate
      };
      const bodyJson = JSON.stringify(bodyForm);
      data && formData.append('body', bodyJson);
      invoice && formData.append('invoice', invoice);
      proofOfPayment && formData.append('proofOfPayment', proofOfPayment);
      await updateOrderStatus(data.orderId, status, formData);
      if (refreshData) {
        await refreshData();
      }
      setUpdateModalInfo(undefined);
      setUpdateDeliveryModal(undefined);
      setLoadingUpdate(false);
    } catch (error) {
      setLoadingUpdate(false);
    }
  };

  const getDataAndClose = () => {
    getList();
    setUpdateModalInfo(undefined);
    setUpdateDeliveryModal(undefined);
  };

  useEffect(() => {
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersList]);
  if (loadingData) {
    return (
      <div className="checkLoadingShoppingList">
        <GenericLoader borderWidth={6} sizeLoader={50} />
      </div>
    );
  }
  return (
    <>
      <DragDropContext
        onDragEnd={result => {
          const { source, destination } = result;
          if (
            source &&
            destination &&
            destination.droppableId === 'confirm-list' &&
            source.droppableId === 'payment-list'
          ) {
            updateConfirm(source.index);
          } else if (
            source &&
            destination &&
            destination.droppableId === 'delivery-process-list' &&
            source.droppableId === 'confirm-list'
          ) {
            updateDeliveryProcess(source.index);
          } else if (
            source &&
            destination &&
            destination.droppableId === 'cancel-list' &&
            source.droppableId === 'payment-list'
          ) {
            updateCancelList(source.index);
          } else if (
            source &&
            destination &&
            destination.droppableId === 'payment-list' &&
            source.droppableId === 'cancel-list'
          ) {
            restorOrderToConfirm(source.index);
          } else if (
            source &&
            destination &&
            destination.droppableId === 'cancel-list' &&
            source.droppableId === 'confirm-list'
          ) {
            cancelOriginConfirmList(source.index);
          }
        }}
      >
        <div id="shoppingList">
          {!!updateModalInfo && (
            <ModalConfirmPayment
              updateData={updateModalInfo}
              actionUpdate={(data: IProductList) => {
                updateData({ data, status: 5 });
              }}
              getGeneralData={getDataAndClose}
              loadingUpdate={loadingUpdate}
            />
          )}
          {!!updateDeliveryModal && <></>}
          <Droppable droppableId="payment-list">
            {droppableProvided => (
              <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef} className="paymentProcess">
                {paymentList.map((item: IProductList, index: number) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {draggableProvided => (
                      <div
                        className="centerCard"
                        {...draggableProvided.draggableProps}
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.dragHandleProps}
                      >
                        <OrderCardInListing refreshData={refreshData} data={item} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
          <Droppable droppableId="confirm-list">
            {droppableProvided => (
              <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef} className="confirmedProcess">
                {confirmList.map((item: IProductList, index: number) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {draggableProvided => (
                      <div
                        className="centerCard"
                        {...draggableProvided.draggableProps}
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.dragHandleProps}
                      >
                        <OrderCardInListing refreshData={refreshData} data={item} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>

          <div className="deliverySuccess">
            {deliverySuccessList.map((item: IProductList, index: number) => (
              <div className="centerCard" key={`delivery-order-card-"${item.id}-index-${index}`}>
                <OrderCardInListing refreshData={refreshData} data={item} />
              </div>
            ))}
          </div>
        </div>

        <div id="horizontalList">
          <div className="titleCancel">{`Cancelados (${cancelList?.length || 0})`}</div>
          <Droppable droppableId="cancel-list" direction="horizontal">
            {droppableProvided => (
              <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef} className="horizontalCards">
                {cancelList.map((item: IProductList, index: number) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {draggableProvided => (
                      <div
                        className="horizontalCard"
                        {...draggableProvided.draggableProps}
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.dragHandleProps}
                      >
                        <OrderCardInListing refreshData={refreshData} data={item} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    </>
  );
};

export default ShoppingList;
